import { Check, ChevronDown, ChevronRight, ChevronUp, Maximize2, TriangleAlert, X } from "lucide-react";
import { CSSProperties } from "react";
import { Link, useSearchParams } from "react-router";

import { StatusPill, type StatusPillTone } from "nazare-ui/components";
import { PageSection, PageSectionAction } from "nazare-ui/components/page-section";
import { useSortableRows } from "nazare-ui/utils";

import {
  formatCostUsd,
  formatDurationMs,
  formatRelativeToNow,
  formatTokens,
  SessionExpiredError,
  useQaAggregates,
  useQaRunsList,
  type AggregateWindow,
  type RunLifecycleStatus,
  type RunSummary,
} from "../../../qa-automation-api";
import { SkeletonBlock } from "../../../skeleton-block";
import { SkeletonRow } from "../../../skeleton-row";
import styles from "./recent-runs.module.css";

/**
 * Bottom half of the QA automation dashboard (`/admin/qa-automation`): the
 * "5 slowest tests" / "Tests with the highest failure rate" ranked cards,
 * and the "Recent runs" table. Every section is backed by the QA API.
 */

// ─────────────────────────────────────────────────────────────────────────
// Ranked lists — "5 slowest tests" / "Tests with the highest failure rate"
// ─────────────────────────────────────────────────────────────────────────

type RankedRow = {
  primary: string;
  context: string;
  primaryValue: string;
  secondaryValue: string;
  /** 0..1, how far the inline bar fills. */
  barFraction: number;
  barTone: "duration" | "fail";
};

function RankedList({ rows }: { rows: RankedRow[] }) {
  return (
    <ol className={styles.rankedList}>
      {rows.map((row, idx) => (
        <li key={row.primary} className={styles.rankedRow} title={row.primary}>
          <span className={styles.rankedIndex}>{idx + 1}</span>
          <div className={styles.rankedMain}>
            <div className={styles.rankedPrimary}>{row.primary}</div>
            {row.context && <div className={styles.rankedContext}>{row.context}</div>}
            <div className={styles.rankedBarTrack}>
              <div
                className={
                  row.barTone === "fail"
                    ? `${styles.rankedBarFill} ${styles.rankedBarFail}`
                    : `${styles.rankedBarFill} ${styles.rankedBarDuration}`
                }
                style={{ width: `${Math.round(Math.max(0, Math.min(1, row.barFraction)) * 100)}%` }}
              />
            </div>
          </div>
          <div className={styles.rankedValues}>
            <div className={styles.rankedPrimaryValue}>{row.primaryValue}</div>
            <div className={styles.rankedSecondaryValue}>{row.secondaryValue}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Shimmering placeholder shaped like a real ranked row while data loads. */
function RankedRowSkeleton({ index }: { index: number }) {
  return (
    <li className={styles.rankedRow} aria-hidden="true">
      <span className={styles.rankedIndex}>{index + 1}</span>
      <div className={styles.rankedMain}>
        <SkeletonBlock height={13} width={`${62 + ((index * 13) % 30)}%`} />
        <div style={{ marginTop: 5 }}>
          <SkeletonBlock height={11} width={`${38 + ((index * 11) % 24)}%`} />
        </div>
        <div style={{ marginTop: 6 }}>
          <SkeletonBlock height={6} width="100%" radius="var(--radius-full)" />
        </div>
      </div>
      <div className={styles.rankedValues}>
        <SkeletonBlock height={13} width={40} />
        <div style={{ marginTop: 5 }}>
          <SkeletonBlock height={11} width={52} />
        </div>
      </div>
    </li>
  );
}

function RankedListSkeleton() {
  return (
    <ol className={styles.rankedList}>
      {Array.from({ length: 5 }, (_, index) => (
        <RankedRowSkeleton key={index} index={index} />
      ))}
    </ol>
  );
}

/** Native navigation also preserves modified clicks and the card transition. */
function MaximiseButton({ title, to }: { title: string; to: string }) {
  return (
    <PageSectionAction
      asChild
      aria-label={`Expand ${title}`}
      title={`Expand ${title}`}
    >
      <Link to={to} viewTransition>
        <Maximize2 aria-hidden="true" />
      </Link>
    </PageSectionAction>
  );
}

function ViewAllFooter({ href }: { href: string }) {
  return (
    <div className={styles.viewAllFooter}>
      <Link className={styles.viewAllLink} to={href} viewTransition>
        <span>View all</span>
        <ChevronRight size={14} aria-hidden />
      </Link>
    </div>
  );
}

function RankedCard({
  title,
  rows,
  emptyLabel,
  viewAllHref,
  viewTransitionName,
  isLoading,
}: {
  title: string;
  rows: RankedRow[];
  emptyLabel: string;
  viewAllHref: string;
  viewTransitionName: string;
  isLoading: boolean;
}) {
  return (
    <PageSection
      className={styles.card}
      style={{ viewTransitionName } as unknown as CSSProperties}
      title={title}
      count={isLoading ? undefined : rows.length}
      actions={<MaximiseButton title={title} to={viewAllHref} />}
      padded
    >
      {isLoading ? (
        <RankedListSkeleton />
      ) : rows.length > 0 ? (
        <RankedList rows={rows} />
      ) : (
        <p className={styles.emptyState}>{emptyLabel}</p>
      )}
      <ViewAllFooter href={viewAllHref} />
    </PageSection>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Recent runs table — real BFF data (`useQaRunsList`), same
// `/qa-automation/runs` endpoint the reference app's own dashboard consumes.
// Shows only the latest RECENT_RUNS_PREVIEW_COUNT (5), same "top-N + View
// all footer" shape as the ranked cards above it, rather than every run the
// hook returns in its own internally-scrolling table — the full list is one
// click away at /qa-automation/recent-runs.
// ─────────────────────────────────────────────────────────────────────────

const STATUS_META: Record<RunLifecycleStatus, { label: string; tone: StatusPillTone }> = {
  queued: { label: "Queued", tone: "neutral" },
  running: { label: "Running", tone: "info" },
  passed: { label: "Passed", tone: "success" },
  failed: { label: "Failed", tone: "error" },
  abandoned: { label: "Abandoned", tone: "neutral" },
};

function EnvPill({ env }: { env: string }) {
  return <span className={styles.envPill}>{env}</span>;
}

function CountsBreakdown({ counts }: { counts: RunSummary["counts"] }) {
  return (
    <span className={styles.counts}>
      <Check size={11} strokeWidth={2.5} aria-hidden />
      {counts.pass}
      <span aria-hidden>·</span>
      <TriangleAlert size={11} strokeWidth={2.5} aria-hidden />
      {counts.passWithSubFinding}
      <span aria-hidden>·</span>
      <X size={11} strokeWidth={2.5} aria-hidden />
      {counts.failRegression}
    </span>
  );
}

/** Matches the ranked cards' own "5 rows, View all footer" shape — this
 * card used to show every run `useQaRunsList()` returned in an internally-
 * scrolling table; now it's a top-5 preview like its siblings, with the
 * full list one click away via `<ViewAllFooter>`. */
const RECENT_RUNS_PREVIEW_COUNT = 5;

function RecentRunsTable() {
  const { data, isLoading, error } = useQaRunsList();
  const runs = data?.runs ?? [];

  // Deliberately still local `useState` (via `useSortableRows`), NOT
  // URL-synced like `../pages/recent-runs.tsx`'s own "When" sort — this
  // card only ever shows the top `RECENT_RUNS_PREVIEW_COUNT` (5) rows of
  // whatever `useQaRunsList()` currently has, so there's no "view" here
  // worth linking to independent of the full `/qa-automation/recent-runs`
  // page one click away via `<ViewAllFooter>`: a link that reproduced this
  // card's sort would still only ever show the same 5 rows a fresh visit
  // already shows by default, and the full page already owns the
  // shareable, URL-synced version of this exact sort for anyone who
  // actually wants to link to a sorted view.
  const { sorted, direction, toggle } = useSortableRows(
    runs,
    (run) => new Date(run.startedAt).getTime(),
    "desc", // most recent first
  );
  const visible = sorted.slice(0, RECENT_RUNS_PREVIEW_COUNT);

  return (
    <PageSection
      className={styles.recentRunsCard}
      style={{ viewTransitionName: "qa-card-recent-runs" } as unknown as CSSProperties}
      title="Recent runs"
      count={isLoading || error ? undefined : visible.length}
      actions={<MaximiseButton title="Recent runs" to="/qa-automation/recent-runs" />}
      padded
    >
      <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>
                  <button
                    type="button"
                    className={styles.thSort}
                    onClick={toggle}
                    aria-label={`Sort by When, currently ${direction === "asc" ? "oldest first" : "newest first"}`}
                  >
                    When
                    {direction === "asc" ? (
                      <ChevronUp size={12} aria-hidden />
                    ) : (
                      <ChevronDown size={12} aria-hidden />
                    )}
                  </button>
                </th>
                <th>Env</th>
                <th>User</th>
                <th>Project</th>
                <th>Status</th>
                <th>Duration</th>
                <th>Est. cost</th>
                <th aria-hidden />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: RECENT_RUNS_PREVIEW_COUNT }, (_, index) => (
                  <SkeletonRow key={index} columns={8} />
                ))
              ) : error ? (
                <tr>
                  <td colSpan={8} className={styles.emptyState}>
                    {error instanceof SessionExpiredError
                      ? "Your session has expired — sign in to the main app in this browser, then reload."
                      : "Couldn't load recent runs."}
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={8} className={styles.emptyState}>
                    No runs in the selected window.
                  </td>
                </tr>
              ) : (
                visible.map((run) => (
                  <tr key={run.runId}>
                    <td>
                      <Link className={styles.whenLink} to={`/qa-automation/run/${run.runId}`}>
                        {formatRelativeToNow(run.startedAt)}
                      </Link>
                    </td>
                    <td>
                      <EnvPill env={run.environment.label} />
                    </td>
                    <td>
                      <span className={styles.userCell}>{run.user.email ?? "(no auth)"}</span>
                    </td>
                    <td className={styles.projectCell}>{run.project}</td>
                    <td>
                      <div className={styles.statusCell}>
                        <StatusPill tone={STATUS_META[run.status].tone}>
                          {STATUS_META[run.status].label}
                        </StatusPill>
                        <CountsBreakdown counts={run.counts} />
                      </div>
                    </td>
                    <td className={styles.durationCell}>{formatDurationMs(run.durationMs)}</td>
                    <td>
                      <div className={styles.costCell}>{formatCostUsd(run.costUsd)}</div>
                      <div className={styles.tokensCell}>
                        {formatTokens(run.tokens?.input)} in · {formatTokens(run.tokens?.output)} out
                      </div>
                    </td>
                    <td className={styles.actionCell}>
                      <Link className={styles.viewLink} to={`/qa-automation/run/${run.runId}`}>
                        View
                        <ChevronRight size={13} aria-hidden />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
      </div>
      <ViewAllFooter href="/qa-automation/recent-runs" />
    </PageSection>
  );
}

/**
 * "5 slowest tests" / "Tests with the highest failure rate" ranked cards
 * plus the "Recent runs" table — the bottom half of the QA automation
 * admin dashboard (`/admin/qa-automation`). The aggregate rankings and run
 * table are both populated from live BFF responses.
 */
export function QaAutomationRecentRuns() {
  const [searchParams] = useSearchParams();
  const requestedWindow = searchParams.get("window");
  const window: AggregateWindow = ["today", "7d", "30d", "6m", "all"].includes(requestedWindow ?? "")
    ? (requestedWindow as AggregateWindow)
    : "30d";
  const aggregates = useQaAggregates(window);
  const slowest = (aggregates.data?.perTestAvgDurationMs ?? []).slice(0, 5);
  const maxDuration = slowest[0]?.avgMs ?? 1;
  const slowestRows: RankedRow[] = slowest.map((row) => ({
    primary: row.primary || row.title,
    context: row.context,
    primaryValue: formatDurationMs(row.avgMs),
    secondaryValue: `${row.runs} run${row.runs === 1 ? "" : "s"}`,
    barFraction: row.avgMs / maxDuration,
    barTone: "duration",
  }));
  const failureRows: RankedRow[] = (aggregates.data?.perTestFailureRate ?? []).slice(0, 5).map((row) => ({
    primary: row.primary || row.title,
    context: row.context,
    primaryValue: `${row.failPct.toFixed(1)}%`,
    secondaryValue: `${row.failCount}/${row.runs} runs`,
    barFraction: row.failPct / 100,
    barTone: "fail",
  }));

  return (
    <div className={styles.stack}>
      <div className={styles.rankedGrid}>
        <RankedCard
          title="5 slowest tests"
          rows={slowestRows}
          emptyLabel="No test runs in the selected window."
          viewAllHref="/qa-automation/slowest-tests"
          viewTransitionName="qa-card-slowest-tests"
          isLoading={aggregates.isLoading}
        />
        <RankedCard
          title="Tests with the highest failure rate"
          rows={failureRows}
          emptyLabel="No failures in the selected window."
          viewAllHref="/qa-automation/highest-failure-rate"
          viewTransitionName="qa-card-highest-failure-rate"
          isLoading={aggregates.isLoading}
        />
      </div>
      <RecentRunsTable />
    </div>
  );
}
